"use client";

import React, { useState } from "react";
import { FeaturedContentBlock, type ImageComponent } from "fansunited-sports-ui";
import { PillToggle } from "@/components/ui/controls/PillToggle";
import { FillImage } from "@/components/ui/FillImage";
import { MediaVariantsProvider } from "@/components/ui/MediaVariantsProvider";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { getPostImageUrl } from "@/components/ui/PostImage";
import type { Post } from "@/payload-types";

type NewsTab = "featured" | "latest";

export interface FeaturedPostsCarouselProps {
    /** Editorially curated featured posts (from Lists collection). May be empty. */
    featuredPosts: Post[];
    /** General latest posts to use as fallback when no featured-tab is active. */
    latestPosts: Post[];
    /** The first hero image is the page's LCP candidate: eager load + `fetchpriority="high"`. Only the first hero on a page. */
    priority?: boolean;
}

/**
 * FeaturedPostsCarousel — Spotlight-variant hero with optional "Featured / Latest" tab toggle.
 *
 * When `featuredPosts` is non-empty, shows a tab strip so the user can switch between
 * the curated picks and latest news. When empty, renders the latest posts only (no tabs).
 *
 * Renders nothing when both arrays are empty.
 */
export function FeaturedPostsCarousel({ featuredPosts, latestPosts, priority = false }: FeaturedPostsCarouselProps) {
    const nav = useNavigation();
    const labels = useLabels();

    const hasFeaturedPosts = featuredPosts.length > 0;
    const [activeTab, setActiveTab] = useState<NewsTab>(hasFeaturedPosts ? "featured" : "latest");

    const mapPostsToFeaturedItems = (posts: Post[]) =>
        posts.slice(0, 6).map((post) => ({
            content: {
                id: String(post.id),
                type: "article" as const,
                link: nav.post(post),
                title: post.title,
                description: post.summary || undefined,
                image: {
                    url: getPostImageUrl(post, "medium"),
                    alt: post.title,
                },
                gradient: "linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.5) 30%, transparent 50%)",
            },
        }));

    const displayedPosts = activeTab === "featured" ? featuredPosts : latestPosts;
    const featuredItems = mapPostsToFeaturedItems(displayedPosts);

    if (featuredItems.length === 0) return null;

    return (
        <section>
            {/* Header with title and tabs */}
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold">{labels.homePage.latestNews}</h2>
                {hasFeaturedPosts && (
                    <PillToggle
                        options={[
                            { id: "featured" as const, label: labels.homePage.featured },
                            { id: "latest" as const, label: labels.homePage.latest },
                        ]}
                        activeId={activeTab}
                        onChange={setActiveTab}
                        size="sm"
                    />
                )}
            </div>

            {/* Featured Content Block */}
            <div>
                <MediaVariantsProvider
                    media={displayedPosts.map((p) => p.heroImage)}
                    sizes="(max-width: 1024px) 100vw, 66vw"
                >
                    <FeaturedContentBlock
                        variant="spotlight"
                        items={featuredItems}
                        ImageComponent={FillImage as ImageComponent}
                        priority={priority}
                        styles={{
                            gap: "sm",
                            mainCardStyles: {
                                shadow: "none",
                                typography: {
                                    title: "text-xl md:text-3xl font-bold mb-1!",
                                    description: "text-xs md:text-sm text-white/70",
                                },
                            },
                            cardStyles: {
                                shadow: "none",
                                typography: {
                                    title: "text-xl font-bold mb-1!",
                                    description: "text-xs md:text-sm text-white/70",
                                },
                            },
                        }}
                    />
                </MediaVariantsProvider>
            </div>
        </section>
    );
}
